/**
 * Putting an AI image's generation details back onto its image node.
 *
 * The per-image "regenerate" control needs two attributes on the node:
 * `imageOpportunity` (the prompt, alt text, aspect ratio…) and `articleId`.
 * The article is stored as markdown, which drops both, so they are kept in a
 * separate map keyed by image src and restored onto the nodes by src.
 *
 * That restore has to run after EVERY time the editor's content is replaced
 * from markdown — not only on load. Applying a fact-check finding, switching
 * between the original and humanized text, and the humanize stream all call
 * `setContent` with markdown-built HTML; without a restore after them the
 * images lose their attributes and the button disappears until a reload.
 */

export interface ImageNodeAt {
  pos: number;
  attrs: Record<string, any>;
}

export interface ImageAttrsUpdate {
  pos: number;
  attrs: Record<string, any>;
}

/**
 * Which image nodes need their details put back, and with what.
 *
 * Only nodes that have a `src` found in the map and are missing the details for
 * THIS article are touched — so running it again changes nothing, and an image
 * the map does not know (a pasted or uploaded one) is left alone.
 */
export function planImageOpportunityRestore(
  images: ImageNodeAt[],
  map: Record<string, any> | null | undefined,
  articleId: string | null | undefined
): ImageAttrsUpdate[] {
  if (!map || !articleId) return [];
  const updates: ImageAttrsUpdate[] = [];
  for (const { pos, attrs } of images) {
    const src = attrs?.src;
    if (!src) continue;
    const opportunity = map[src];
    if (!opportunity) continue;
    if (attrs.imageOpportunity && attrs.articleId === articleId) continue; // already set
    updates.push({ pos, attrs: { ...attrs, imageOpportunity: opportunity, articleId } });
  }
  return updates;
}

/** The smallest slice of a TipTap editor this needs — kept narrow so it can be tested without a DOM. */
export interface RestorableEditor {
  state: {
    doc: { descendants: (fn: (node: any, pos: number) => boolean | void) => void };
    tr: {
      setNodeMarkup: (pos: number, type: any, attrs: Record<string, any>) => any;
      setMeta: (key: string, value: any) => any;
    };
  };
  view: { dispatch: (tr: any) => void };
}

/**
 * Restore the details onto the editor's image nodes in one transaction.
 *
 * The transaction is programmatic: `programmatic.current` is held true while it
 * is dispatched so the editor's update handler does not treat it as an edit and
 * save, and it is kept out of the undo history. Returns how many images were
 * updated.
 */
export function restoreImageOpportunities(
  editor: RestorableEditor | null | undefined,
  map: Record<string, any> | null | undefined,
  articleId: string | null | undefined,
  programmatic: { current: boolean }
): number {
  if (!editor || !map || Object.keys(map).length === 0) return 0;
  const images: ImageNodeAt[] = [];
  editor.state.doc.descendants((node: any, pos: number) => {
    if (node?.type?.name === "image") images.push({ pos, attrs: node.attrs || {} });
  });
  const updates = planImageOpportunityRestore(images, map, articleId);
  if (updates.length === 0) return 0;

  let tr = editor.state.tr;
  for (const { pos, attrs } of updates) {
    tr = tr.setNodeMarkup(pos, undefined, attrs);
  }
  // Not an edit anybody made, so not something to undo: left in history, the
  // first Ctrl+Z after applying a finding would only take the details off the
  // images again — the button vanishes and the finding stays.
  tr = tr.setMeta("addToHistory", false);
  const wasProgrammatic = programmatic.current;
  programmatic.current = true;
  try {
    editor.view.dispatch(tr);
  } finally {
    programmatic.current = wasProgrammatic;
  }
  return updates.length;
}
